import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { generatePath, useNavigate, useParams } from 'react-router-dom'
import EmploymentForm from '../../components/Form/EmploymentForm'
import Loading from '../../components/Loading/Loading'
import navigation from '../../config/navigation'
import { useAppDispatch, useNotification } from '../../store/redux/hooks'
import { useEditContractMutation, useGetContractQuery } from '../../store/redux/reducers/contractSlice'
import { getCurrentUser, getIsManagementOrAdministrator } from '../../store/redux/reducers/currentUserSlice'
import { useGetDictionariesQuery } from '../../store/redux/reducers/dictionarySlice'
import { PageIndex, setPageIndex, setPageName } from '../../store/redux/reducers/pageNameSlice'
import { RootState } from '../../store/redux/store'
import { EmploymentFormValues } from '../../store/types/form/EmploymentFormValues'
import { ContractLightDTO } from '../../store/types/models/Contract'
import { createErrorMessage } from '../../store/utils'
import { mapFormVacationPeriodsToModifyDTO } from '../../store/utils/employmentUtils'

const EditContractView = () => {
  const { userId, contractId } = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { displayNotification } = useNotification()
  const currentUser = useSelector(getCurrentUser)
  const isManagementOrAdministrator = useSelector((state: RootState) => getIsManagementOrAdministrator(state))
  const employeeId = userId ?? currentUser?.id

  const { data: contract, isLoading } = useGetContractQuery({ id: contractId as string }, { skip: !contractId })
  const [editContract] = useEditContractMutation()

  const { data: options } = useGetDictionariesQuery({
    page: 0,
    size: 20,
    group: 'EMPLOYMENT_TYPE',
  })

  const employeeName = contract?.employeeFullName

  useEffect(() => {
    dispatch(setPageName(t('page.name.editContract')))
    dispatch(setPageIndex(PageIndex.USERS))
  }, [dispatch, t])

  const handleClose = () => {
    userId ? navigate(generatePath(navigation.user, { userId })) : navigate(navigation.users)
  }

  const handleSubmit = async (values: EmploymentFormValues, allowNavigation: () => void) => {
    if (!isManagementOrAdministrator) return
    if (!employeeId) return
    if (!options) return
    if (!contract) return

    const contractTypeId = options.content.find(v => v.key === values.employmentType)?.id

    if (!contractTypeId) return

    const mappedPeriods = mapFormVacationPeriodsToModifyDTO(values.vacationPeriods)

    try {
      await editContract({
        id: contract.id,
        body: {
          version: contract.version ?? null,
          employmentPercentage: Number(values.employmentPercentage) || 0,
          paidVacationPeriods: mappedPeriods,
          paidVacation: values.paidVacation,
          startDate: values.startDate ? values.startDate.utc(true).toISOString() : '',
          indefiniteDuration: values.indefiniteDuration,
          endDate: values.indefiniteDuration ? null : values.endDate ? values.endDate.utc(true).toISOString() : null,
          contractTypeId: contractTypeId,
          employeeId: employeeId,
          settlementMethod: values.settlementMethod,
        },
      }).unwrap()

      displayNotification({
        message: t('page.employments.notifications.editEmploymentSuccess'),
        type: 'success',
      })

      allowNavigation()
      handleClose()
    } catch (e) {
      const error = createErrorMessage(e)
      displayNotification({
        message: error || t('message.somethingWentWrong'),
        type: 'error',
      })
    }
  }

  const contractLight: ContractLightDTO | undefined =
    contract &&
    ({
      id: contract.id,
      employeeId: contract.employeeId,
      startDate: contract.startDate,
      endDate: contract.endDate,
      settlementMethod: contract.settlementMethod,
      employmentPercentage: contract.employmentPercentage,
      paidVacation: contract.paidVacation,
      contractTypeValue: contract.contractTypeValue,
      paidVacationPeriods: contract.paidVacationPeriods,
    } as ContractLightDTO)

  if (isLoading) {
    return <Loading />
  }

  return (
    <EmploymentForm
      onClose={handleClose}
      onSubmit={handleSubmit}
      contract={contractLight}
      employeeName={employeeName}
    />
  )
}

export default EditContractView
